import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Ship, Mail, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1500687834377-1388ec3c5991?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.access_token, data.user);
      toast.success(`Welcome back, ${data.user.name.split(" ")[0]}!`);
      navigate(data.user.role === "admin" ? "/admin" : from, { replace: true });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Ferry" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/60" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <Ship className="h-8 w-8 text-accent mb-4" />
          <h2 className="font-display font-bold text-3xl tracking-tight">Welcome aboard WaveLine.</h2>
          <p className="text-white/75 mt-2">Sign in to manage bookings and access your e-tickets.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2.5 mb-8">
            <span className="h-9 w-9 rounded-md bg-primary flex items-center justify-center"><Ship className="h-5 w-5 text-white" /></span>
            <span className="font-display font-bold text-lg text-primary">WaveLine</span>
          </Link>
          <h1 className="font-display font-bold text-2xl text-primary tracking-tight">Sign in</h1>
          <p className="text-muted-foreground text-sm mt-1 mb-6">Enter your details to continue</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" placeholder="you@example.com" data-testid="login-email" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9" placeholder="••••••••" data-testid="login-password" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-white font-semibold" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            New here? <Link to="/register" className="text-accent font-semibold" data-testid="go-register-link">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
